import type { ChatModelAdapter, ChatModelRunOptions } from '@assistant-ui/react';
import { API_BASE_URL } from '../config/api';
import { authFetch } from './session';
import { ticketEscapeLink, applyEvent, buildContent, initialStreamState, readEventFrames } from './assistantStream';
import { MAX_APPROVED_TOKENS, approvalTokensOf } from '../lib/assistantConfirmation';
import type { AssistantConfig, AssistantContext } from '../types/assistant';

/**
 * Client of the internal control-plane assistant ("TimeGlobe Intelligence").
 *
 * `GET  /api/assistant/config`  — is the assistant switched on for this account?
 * `POST /api/assistant/stream`  — SSE endpoint; the server holds no session, the
 *                                 running history (and the thread's context) is
 *                                 resent every turn.
 */

const CONFIG_URL = `${API_BASE_URL}/assistant/config`;
const STREAM_URL = `${API_BASE_URL}/assistant/stream`;

/** The assistant is off whenever the server does not clearly say it is on. */
export async function getConfig(): Promise<AssistantConfig> {
  try {
    const response = await authFetch(CONFIG_URL);
    if (response.status !== 200) return { enabled: false };
    const data: unknown = await response.json();
    const enabled = typeof data === 'object' && data !== null && (data as { enabled?: unknown }).enabled === true;
    return { enabled };
  } catch {
    return { enabled: false };
  }
}

interface ApiMessage {
  role: string;
  content: string;
}

/** Flatten an assistant-ui ThreadMessage to the `{role, content}` our API wants. */
function toApiMessage(message: ChatModelRunOptions['messages'][number]): ApiMessage {
  const text = message.content
    .map((part) => (part.type === 'text' ? part.text : ''))
    .join('');
  return { role: message.role, content: text };
}

const isAbort = (error: unknown): boolean => error instanceof Error && error.name === 'AbortError';

const isSessionEnded = (error: unknown): boolean =>
  error instanceof Error && error.message === 'Authentication expired';

const OUT_OF_REACH = 'Der Assistent ist gerade nicht erreichbar.';

const TOO_MANY_REQUESTS =
  'Das waren gerade sehr viele Anfragen. Bitte warte einen Moment und versuche es dann erneut.';

/** The `detail` of a 429 answer, or the same sentence when the body is not readable. */
async function rateLimitText(response: Response): Promise<string> {
  try {
    const data: unknown = await response.json();
    const detail = typeof data === 'object' && data !== null ? (data as { detail?: unknown }).detail : null;
    return typeof detail === 'string' && detail.trim() !== '' ? detail : TOO_MANY_REQUESTS;
  } catch {
    return TOO_MANY_REQUESTS;
  }
}

/**
 * What the owner reads when the request itself fails. A thread from
 * "Problem melden" always gets the way out; elsewhere a retry is the answer.
 */
function failureText(context: AssistantContext | null): string {
  if (context?.origin === 'report_problem') {
    return `${OUT_OF_REACH}\n\nDu kannst trotzdem direkt ein Ticket schreiben – das Gespräch hängt dann schon dran.\n\n${ticketEscapeLink(context.conversation_id)}`;
  }
  return `${OUT_OF_REACH} Bitte versuch es gleich noch einmal.`;
}

/**
 * ChatModelAdapter for the assistant.
 *
 * `getContext` is read on EVERY request, so a thread keeps its context across
 * turns (the server is stateless) and the view can change it between turns.
 *
 * Approval of a prepared change (`approved_tokens`) is bound to the user
 * message that STARTS the run: the "Ja, so umsetzen" click puts the tokens on
 * that very message, a typed message has none. The tokens are read from the
 * last message of THIS run's history — never from a variable the click set
 * earlier — so a run that never starts, an aborted run or a run for another
 * turn cannot pick up or leave behind an approval. On top of that every token
 * is spent when its request is started: a repeated or regenerated run of the
 * same message sends none.
 */
export function createAssistantAdapter(getContext: () => AssistantContext | null): ChatModelAdapter {
  const spentTokens = new Set<string>();

  /** The tokens of the message that starts this run, each handed out once. */
  const takeApprovedTokens = (history: ChatModelRunOptions['messages']): string[] => {
    const fresh = approvalTokensOf(history[history.length - 1]).filter((token) => !spentTokens.has(token));
    const sent = fresh.slice(0, MAX_APPROVED_TOKENS);
    sent.forEach((token) => spentTokens.add(token));
    return sent;
  };

  return {
    async *run({ messages, abortSignal }) {
      // First thing, before anything can be awaited: the request is as good as sent.
      const approvedTokens = takeApprovedTokens(messages);
      const context = getContext();
      let state = initialStreamState();
      try {
        const response = await authFetch(STREAM_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            messages: messages.map(toApiMessage).filter((m) => m.content),
            ...(context ? { context } : {}),
            ...(approvedTokens.length > 0 ? { approved_tokens: approvedTokens } : {}),
          }),
          signal: abortSignal,
        });

        if (response.status === 401) {
          // Final: authFetch already tried to renew the session and told the app.
          throw new Error('Authentication expired');
        }
        if (response.status === 429) {
          // The server's own sentence ("sehr viele Anfragen … warte einen Moment"):
          // "nicht erreichbar" would send the owner looking for an outage.
          state = { ...state, text: await rateLimitText(response) };
          yield { content: buildContent(state) };
          return;
        }
        if (!response.ok || !response.body) {
          throw new Error(`Assistant stream failed (HTTP ${response.status})`);
        }

        for await (const events of readEventFrames(response.body)) {
          for (const event of events) {
            state = applyEvent(state, event, context?.conversation_id);
          }
          yield { content: buildContent(state) };
        }
        // Flush a final snapshot in case the last frame had no trailing blank line.
        yield { content: buildContent(state) };
      } catch (error) {
        // Stop pressed / page left, or the session is over: the runtime and the app handle those.
        if (isAbort(error) || abortSignal.aborted || isSessionEnded(error)) throw error;
        state = { ...state, text: state.text ? `${state.text}\n\n${failureText(context)}` : failureText(context) };
        yield { content: buildContent(state) };
      }
    },
  };
}
